import { useCallback, useEffect, useRef, useState } from 'react'

import { asFailure, type Failure } from '../api/request'
import { fetchLibrary, type LibraryFolder } from '../api/library'
import {
  type SubjectContent,
  addSubject,
  anchorFolder,
  archiveSubject,
  changeSubject,
  fetchSubjectContent,
  fetchSubjects,
  mergeSubject,
  removeSubject,
  setRootFolder,
  unanchorFolder,
} from '../api/subjects'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { Button } from '../ui/Control'
import { Offer } from '../ui/Offer'
import { Failed, Waiting } from '../ui/Status'
import { pathToDocumentWhereverItLies } from './library/place'
import { pathToAppointmentWheneverItIs } from './timetable/place'
import { openedIn, SUBJECTS_AREA } from './subjects/place'
import { SubjectList } from './subjects/SubjectList'
import { SubjectSheet } from './subjects/SubjectSheet'

/**
 * Subjects: what something is *about*, as opposed to where it lies.
 *
 * It has a symbol of its own in the navigation, and that is a decision rather than a default. The
 * external systems deliberately have none — setting one up is something somebody does once a
 * semester — but "I open the subject and everything is together" is the movement before an exam, and
 * therefore one of the most frequent ways into somebody's own material.
 *
 * The area is built around one sentence from the issue: *anchoring a folder is the most effective
 * single act here*. So the empty view offers exactly that, and the sheet says what it means where it
 * happens — everything in the folder and below it, no file touched, and undone by taking the anchor
 * away again.
 */
export function Subjects() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const opened = openedIn(route.path)
  const openId = opened.subjectId
  const announce = useAnnounce()

  const list = useLoad(useCallback((signal: AbortSignal) => fetchSubjects(true, signal), []))
  const library = useLoad(useCallback((signal: AbortSignal) => fetchLibrary(signal), []))
  const [refused, setRefused] = useState<Failure | null>(null)
  const [typed, setTyped] = useState('')
  const field = useRef<HTMLInputElement | null>(null)

  const subjects = list.load.status === 'ready' ? list.load.value : []
  const folders: readonly LibraryFolder[] =
    library.load.status === 'ready' ? library.load.value.folders : []
  const open = subjects.find((subject) => subject.id === openId)

  /*
   * What is in a subject is a second request, made only when one is opened.
   *
   * The list itself carries the counts, so drawing it needs none of this; fetching every subject's
   * content to draw a list would be a request per row.
   */
  const held = useLoad(
    useCallback(
      (signal: AbortSignal): Promise<SubjectContent | null> =>
        openId === null ? Promise.resolve(null) : fetchSubjectContent(openId, signal),
      [openId],
    ),
  )
  const content = held.load.status === 'ready' ? held.load.value : null

  // `#/subjects/new` is the cursor in the field, and nothing else: no row is created, and a reload
  // lands where somebody was rather than on a subject that never existed.
  useEffect(() => {
    if (opened.writing) {
      field.current?.focus()
    }
  }, [opened.writing])

  const go = (subject?: string) => {
    navigate(SUBJECTS_AREA, subject === undefined ? [] : [subject])
  }

  const attempt = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  const create = () => {
    const wanted = typed.trim()
    if (wanted === '') {
      return
    }
    setTyped('')
    void attempt(async () => {
      const made = await addSubject(wanted)
      list.retry()
      announce('Subject created.')
      go(made.id)
    })
  }

  const reload = () => {
    list.retry()
    held.retry()
  }

  return (
    <Lanes
      overview={{
        label: 'Subjects',
        content: (
          <div className="subjects">
            <header className="subjects__head">
              <h2 className="subjects__title">Subjects</h2>
            </header>

            <form
              className="subjects__write"
              onSubmit={(event) => {
                event.preventDefault()
                create()
              }}
            >
              <label className="visually-hidden" htmlFor="new-subject">
                Name a subject
              </label>
              <input
                id="new-subject"
                ref={field}
                className="subjects__field"
                value={typed}
                placeholder="A subject — “Zwangsvollstreckung”"
                onChange={(event) => {
                  setTyped(event.target.value)
                }}
              />
              <Button tone="primary" type="submit" disabled={typed.trim() === ''}>
                Create
              </Button>
            </form>

            {refused !== null && <Failed failure={refused} />}

            {list.load.status === 'waiting' && <Waiting label="Fetching your subjects." />}
            {list.load.status === 'failed' && (
              <Failed
                failure={list.load.failure}
                onRetry={list.retry}
                title="The subjects did not load"
              />
            )}

            {list.load.status === 'ready' && subjects.length === 0 && (
              <Offer
                title="What your material is about"
                lead="A subject is the bracket over everything that belongs together — the scripts, the notes, the cards and the open points. A folder says where something lies; a subject says what it is about."
                points={[
                  'Anchor a folder to a subject and everything in it belongs, without touching a single file.',
                  'Anything can belong to several subjects at once, or to none.',
                  'A subject is permanent. What is over is archived and stays complete.',
                ]}
              />
            )}

            {subjects.length > 0 && <SubjectList subjects={subjects} openId={openId} onOpen={go} />}
          </div>
        ),
      }}
      detail={
        open === undefined
          ? null
          : {
              label: 'Subject',
              content: (
                <SubjectSheet
                  // Keyed by what the service last said: a change arriving is a new surface rather
                  // than one being written into from outside.
                  key={`${open.id}-${open.updatedAt}`}
                  subject={open}
                  content={content}
                  all={subjects}
                  folders={folders}
                  onChange={(name, description, descriptionMarkup) => {
                    void attempt(async () => {
                      await changeSubject(open.id, name, description, descriptionMarkup)
                      list.retry()
                      announce('Saved.')
                    })
                  }}
                  onArchive={(archived) => {
                    void attempt(async () => {
                      await archiveSubject(open.id, archived)
                      list.retry()
                      announce(archived ? 'Archived.' : 'Running again.')
                    })
                  }}
                  onMerge={(from) => {
                    void attempt(async () => {
                      await mergeSubject(open.id, from)
                      reload()
                      announce('Merged.')
                    })
                  }}
                  onRemove={() => {
                    void attempt(async () => {
                      await removeSubject(open.id)
                      list.retry()
                      go()
                      announce('Deleted.')
                    })
                  }}
                  onAnchor={(folderId) => {
                    void attempt(async () => {
                      await anchorFolder(open.id, folderId)
                      reload()
                      announce('Folder anchored.')
                    })
                  }}
                  onUnanchor={(folderId) => {
                    void attempt(async () => {
                      await unanchorFolder(open.id, folderId)
                      reload()
                      announce('Folder removed.')
                    })
                  }}
                  onRootFolder={(folderId) => {
                    void attempt(async () => {
                      await setRootFolder(open.id, folderId)
                      list.retry()
                      announce('Saved.')
                    })
                  }}
                  onOpenContent={(kind, id) => {
                    const area = AREA_OF[kind]
                    if (area !== undefined) {
                      navigate(area, pathInside(kind, id))
                    }
                  }}
                  onClose={() => {
                    go()
                  }}
                />
              ),
            }
      }
    />
  )
}

/**
 * Where each kind of content lives.
 *
 * Written once here rather than at every row: "a note opens on the pad and a document in the
 * library" is one sentence, and the second copy of it is where two screens start to disagree.
 */
const AREA_OF: Readonly<Record<string, string>> = {
  documents: 'library',
  notes: 'notes',
  tasks: 'tasks',
  flashcards: 'cards',
  boards: 'desk',
  appointments: 'timetable',
}

/**
 * The address inside that area, out of the id and nothing else.
 *
 * A subject knows what its content is and what it is called, and not where each of them is filed —
 * which is fine everywhere but the two areas that address a thing by where it is as well as by its
 * id. In the library an id on its own reads as a folder, and a folder nobody ever made is an empty
 * shelf; in the plan it reads as a view, and shows this week with nothing open. Both are told "and I
 * do not know where" and correct the address once they have asked.
 */
function pathInside(kind: string, id: string): string[] {
  switch (kind) {
    case 'documents':
      return pathToDocumentWhereverItLies(id)
    case 'appointments':
      return pathToAppointmentWheneverItIs(id)
    // The cards area addresses one card under `card/`, because the first step of its address is a tab.
    case 'flashcards':
      return ['card', id]
    default:
      return [id]
  }
}
